import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Box } from "@mui/material";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { useMutation, useQuery } from "react-query";
import { ApplicationsService, SystemSettingsService, AppBundle } from "gen/clients";

interface Props {
  applicationId: string;
  presetId: string;
  presetName: string;
  onClose: () => void;
  onRefresh: () => void;
  onPresetDeleted: () => void;
}

const DeletePresetDialog: React.FC<Props> = ({
  applicationId,
  presetId,
  presetName,
  onClose,
  onRefresh,
  onPresetDeleted
}) => {
  const [usageError, setUsageError] = React.useState<string>("");

  // Fetch application data
  const {
    data: application,
    isLoading: isApplicationLoading,
    error: applicationError
  } = useQuery(["getApplication", applicationId], {
    queryFn: () => ApplicationsService.getApplication({ id: applicationId }),
    refetchOnMount: true
  });

  // Fetch app bundles to check usage
  const {
    data: systemSettings,
    isLoading: isSystemSettingsLoading,
    error: systemSettingsError
  } = useQuery(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    refetchOnMount: true
  });

  // Delete mutation
  const {
    mutateAsync,
    isLoading: isMutating,
    error: mutationError
  } = useMutation(ApplicationsService.updateApplication);

  // Check if preset is in use
  const appBundlesUsingPreset = React.useMemo(() => {
    if (!systemSettings?.appBundles) {
      return [];
    }

    return systemSettings.appBundles.filter((bundle: AppBundle) =>
      bundle.applications?.some(app => app.applicationSettingsPresetId === presetId)
    );
  }, [systemSettings?.appBundles, presetId]);

  const isPresetInUse = appBundlesUsingPreset.length > 0;

  const onDelete = React.useCallback(async () => {
    if (!application) {
      return;
    }

    // Check usage before deletion
    if (isPresetInUse) {
      const bundleNames = appBundlesUsingPreset.map(bundle => bundle.name).join(", ");
      setUsageError(
        `Cannot delete preset '${presetName}' because it is currently being used in the following app bundles: ${bundleNames}. Please remove the preset from these app bundles before deleting.`
      );
      return;
    }

    try {
      // Remove the preset from the application's settingPresets array
      const updatedPresets = (application.settingPresets || []).filter(preset => preset.id !== presetId);

      // Update application with the preset removed
      const updatedApplication = {
        ...application,
        settingPresets: updatedPresets
      };

      await mutateAsync({
        id: application.id,
        requestBody: updatedApplication
      });

      // Call callbacks after successful API call
      onPresetDeleted();
      onRefresh();
      onClose();
    } catch (_error) {
      // Error will be handled by the mutation error state
    }
  }, [
    application,
    isPresetInUse,
    appBundlesUsingPreset,
    presetName,
    presetId,
    mutateAsync,
    onPresetDeleted,
    onRefresh,
    onClose
  ]);

  // Show loading state while fetching data
  if (isApplicationLoading || isSystemSettingsLoading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="sm">
        <DialogTitle>Delete Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ minHeight: 100, justifyContent: "center", alignItems: "center" }}>
            Loading...
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error if failed to load data
  if (applicationError || systemSettingsError) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="sm">
        <DialogTitle>Delete Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Typography color="error">Failed to load preset information. Please try again.</Typography>
            {applicationError && <ApiErrorMessage apiError={applicationError} />}
            {systemSettingsError && <ApiErrorMessage apiError={systemSettingsError} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="sm">
      <DialogTitle>Delete Preset</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography>
            Are you sure you want to delete the preset <strong>'{presetName}'</strong>? This action cannot be undone.
          </Typography>

          {usageError && (
            <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}>
              <Typography color="error.contrastText" variant="body2">
                {usageError}
              </Typography>
            </Box>
          )}

          {mutationError && <ApiErrorMessage apiError={mutationError} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton color="error" variant="contained" onClick={onDelete} isLoading={isMutating}>
          Delete
        </LoadingButton>
        <Button onClick={onClose} disabled={isMutating}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { DeletePresetDialog };
